import {
  getAll,
  getBoolean,
  getNumber,
  getString,
  RemoteConfig,
} from '@firebase/remote-config'
import { ThunkAction } from 'redux-thunk'
import { Environments } from 'src/configurations'
import {
  RemoteConfigIssue,
  RemoteConfigKeys,
  remoteConfigKeys,
  RemoteConfigModel,
  RemoteConfigResponse,
} from 'src/models/remoteConfig.models'
import {
  getFirebaseRemoteConfig,
  GetRemoteConfigActions,
} from 'src/redux/actions/session.actions'
import { RootState } from 'src/redux/createRootReducer'
import { safeToStringArray } from 'src/utils/string.utils'

// These rollout flags default to false in the client, so clients can ship
// before Firebase is configured without surfacing a Dev-config error. Once
// Firebase serves a key, its value still overrides the default.
const optionalRemoteConfigKeys = new Set<RemoteConfigKeys>([
  'enable_transaction_free_text_save_on_blur',
  'show_extend_brand_french_legal',
])

export const getFeatureFlagsFromRemoteConfigAction = (
  remoteConfig: RemoteConfig,
  cookieConfig?: Partial<RemoteConfigResponse>,
  env: Environments = Environments.Dev,
): ThunkAction<void, RootState, void, GetRemoteConfigActions> => {
  let configIssue: RemoteConfigIssue = RemoteConfigIssue.None

  const logIssueForDev = (
    message: string,
    severity: RemoteConfigIssue,
  ): void => {
    if (env === Environments.Prod) return
    switch (severity) {
      case RemoteConfigIssue.Error:
      case RemoteConfigIssue.Warning:
      case RemoteConfigIssue.None:
        /* eslint-disable-next-line no-console */
        console.error(message)
        break
    }
    configIssue = Math.max(configIssue, severity)
  }

  const unusedKeys: string[] = remoteConfigKeys.filter(
    (key) => !optionalRemoteConfigKeys.has(key),
  )
  const remoteConfigResponseSafe = Object.keys(getAll(remoteConfig)).reduce(
    (acc, key) => {
      if (remoteConfigKeys.includes(key as RemoteConfigKeys)) {
        if (unusedKeys.indexOf(key) > -1) {
          unusedKeys.splice(unusedKeys.indexOf(key), 1)
        }
        const keyType = typeof new RemoteConfigModel()[
          key as keyof RemoteConfigModel
        ]

        switch (keyType) {
          case 'boolean': {
            const rawValue = getString(remoteConfig, key)
            if (rawValue !== 'true' && rawValue !== 'false') {
              logIssueForDev(
                `Remote Config key from Firebase '${key}' was defined locally as type boolean but did not return as 'true' or 'false' from firebase`,
                RemoteConfigIssue.Error,
              )
            }
            // @ts-ignore safe cast because we checked if the key is in RemoteConfigKeys, and we've verified the type already
            acc[key] = getBoolean(remoteConfig, key)
            break
          }
          case 'number': {
            const rawValue = getString(remoteConfig, key)
            if (isNaN(Number(rawValue))) {
              logIssueForDev(
                `Remote Config key from Firebase '${key}' was defined locally as type number but could not be parsed as a number`,
                RemoteConfigIssue.Error,
              )
            }
            // @ts-ignore safe cast because we checked if the key is in RemoteConfigKeys, and we've verified the type already
            acc[key] = getNumber(remoteConfig, key)
            break
          }
          case 'string': {
            // @ts-ignore safe cast because we checked if the key is in RemoteConfigKeys, and we've verified the type already
            acc[key] = getString(remoteConfig, key)
            break
          }
          case 'object': {
            // Currently we only support string[] for json objects in remote config
            const remoteConfigValue = safeToStringArray(
              JSON.parse(getString(remoteConfig, key)),
            )
            if (remoteConfigValue.length === 0) {
              logIssueForDev(
                `Remote Config key from Firebase '${key}' was of type object but was empty`,
                RemoteConfigIssue.Warning,
              )
            }
            // @ts-ignore safe cast because we checked if the key is in RemoteConfigKeys, and we've verified the type already
            acc[key] = remoteConfigValue
            break
          }
          default: {
            logIssueForDev(
              `Remote Config key from Firebase '${key}' was of unexpected type ${keyType}`,
              RemoteConfigIssue.Error,
            )
            break
          }
        }
      } else {
        logIssueForDev(
          `Remote Config key from Firebase '${key}' was not found in RemoteConfigKeys`,
          RemoteConfigIssue.Error,
        )
      }
      return acc
    },
    {} as RemoteConfigResponse,
  )

  if (unusedKeys.length > 0) {
    logIssueForDev(
      `Remote Config keys from local that were not found in Firebase: ${unusedKeys.join(', ')}`,
      RemoteConfigIssue.Error,
    )
  }

  return getFirebaseRemoteConfig(
    RemoteConfigModel.fromResponse(remoteConfigResponseSafe),
    cookieConfig,
    configIssue,
  )
}
